import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  blogPostCreateSchema,
  type BlogPostCreateInput,
  type BlogPost,
} from '@inafana/shared';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RichTextEditor } from '@/components/RichTextEditor';
import { useToast } from '@/components/ui/use-toast';

const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph' }] } as const;

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export function BlogEditor(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();

  const existing = useQuery({
    queryKey: ['admin', 'blog', id],
    queryFn: () => api<{ post: BlogPost }>(`/admin/blog/${id ?? ''}`),
    enabled: isEdit,
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<BlogPostCreateInput>({
    resolver: zodResolver(blogPostCreateSchema),
    defaultValues: {
      slug: '',
      title: '',
      excerpt: '',
      content: EMPTY_DOC,
      status: 'DRAFT',
    },
  });

  useEffect(() => {
    if (existing.data?.post) {
      const p = existing.data.post;
      reset({
        slug: p.slug,
        title: p.title,
        excerpt: p.excerpt,
        content: p.content,
        status: p.status,
      });
    }
  }, [existing.data, reset]);

  const title = watch('title');
  const slug = watch('slug');
  useEffect(() => {
    if (!isEdit && !slug && title) {
      setValue('slug', slugify(title));
    }
  }, [title, slug, isEdit, setValue]);

  const save = useMutation({
    mutationFn: async (data: BlogPostCreateInput) => {
      if (isEdit) {
        return api<{ post: BlogPost }>(`/admin/blog/${id ?? ''}`, {
          method: 'PATCH',
          body: data,
        });
      }
      return api<{ post: BlogPost }>('/admin/blog', { method: 'POST', body: data });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'blog'] });
      toast({ title: isEdit ? 'Post updated' : 'Post created' });
      navigate('/admin/blog');
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'Save failed';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  const status = watch('status');

  return (
    <form
      onSubmit={handleSubmit((d) => save.mutate(d))}
      className="space-y-6"
    >
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{isEdit ? 'Edit post' : 'New post'}</h1>
        <div className="flex items-center gap-3">
          <Label htmlFor="status" className="flex items-center gap-2">
            <Switch
              id="status"
              checked={status === 'PUBLISHED'}
              onCheckedChange={(v) => setValue('status', v ? 'PUBLISHED' : 'DRAFT')}
            />
            <span className="text-sm">{status === 'PUBLISHED' ? 'Published' : 'Draft'}</span>
          </Label>
          <Button type="button" variant="ghost" onClick={() => navigate('/admin/blog')}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </header>

      <div className="grid gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="title">Title</Label>
          <Input id="title" {...register('title')} />
          {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="slug">Slug</Label>
          <Input id="slug" {...register('slug')} />
          {errors.slug && <p className="text-xs text-destructive">{errors.slug.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="excerpt">Excerpt</Label>
          <Textarea id="excerpt" rows={3} {...register('excerpt')} />
          {errors.excerpt && <p className="text-xs text-destructive">{errors.excerpt.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label>Content</Label>
          <Controller
            control={control}
            name="content"
            render={({ field }) => (
              <RichTextEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </div>
      </div>
    </form>
  );
}
